import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postsAPI, categoriesAPI } from '../services/api';
import { Save, ArrowLeft } from 'lucide-react';

const PostEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState({
    title: '',
    content: '',
    excerpt: '',
    tags: [],
    status: 'draft',
    categories: []
  });
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postRes, catRes] = await Promise.all([
          postsAPI.getPostById(id),
          categoriesAPI.getAllCategories()
        ]);
        const data = postRes.data;
        setPost({
          title: data.title || '',
          content: data.content || '',
          excerpt: data.excerpt || '',
          tags: Array.isArray(data.tags) ? data.tags : [],
          status: data.status || 'draft',
          categories: Array.isArray(data.category_ids) ? data.category_ids : []
        });
        const catsData = Array.isArray(catRes.data)
          ? catRes.data
          : (Array.isArray(catRes.data?.data) ? catRes.data.data : []);
        setAllCategories(catsData);
      } catch (err) {
        alert('Erro ao carregar post para edição: ' + (err.message || ''));
        navigate('/posts');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleChange = (field, value) => {
    setPost(prev => ({ ...prev, [field]: value }));
  };

  const handleTagInput = (value) => {
    const tags = value.split(',').map(t => t.trim()).filter(Boolean);
    setPost(prev => ({ ...prev, tags }));
  };

  const handleCategoryToggle = (catId) => {
    setPost(prev => {
      const current = new Set(prev.categories.map(Number));
      if (current.has(Number(catId))) {
        current.delete(Number(catId));
      } else {
        current.add(Number(catId));
      }
      return { ...prev, categories: Array.from(current) };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await postsAPI.updatePost(id, post);
      alert('Post atualizado com sucesso!');
      navigate('/posts');
    } catch (err) {
      alert('Erro ao salvar post: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="posts-loading">
        <div className="loading-spinner"></div>
        <p>Carregando dados do post...</p>
      </div>
    );
  }

  return (
    <div className="post-edit">
      <div className="edit-header">
        <button className="page-btn" onClick={() => navigate('/posts')}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h2>✏️ Editar Post</h2>
      </div>

      <div className="edit-form">
        <div className="form-group">
          <label>Título</label>
          <input
            type="text"
            value={post.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Título do post"
          />
        </div>

        <div className="form-group">
          <label>Resumo (excerpt)</label>
          <input
            type="text"
            value={post.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            placeholder="Breve resumo do post"
          />
        </div>

        <div className="form-group">
          <label>Conteúdo (HTML)</label>
          <textarea
            rows={12}
            value={post.content}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="Conteúdo em HTML"
          ></textarea>
        </div>

        <div className="form-group">
          <label>Tags (separadas por vírgula)</label>
          <input
            type="text"
            value={post.tags.join(', ')}
            onChange={(e) => handleTagInput(e.target.value)}
            placeholder="ex: tecnologia, IA, dicas"
          />
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            value={post.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            <option value="">Não enviado</option>
            <option value="draft">Rascunho</option>
            <option value="published">Publicado</option>
            <option value="scheduled">Agendado</option>
          </select>
        </div>

        <div className="form-group">
          <label>Categorias</label>
          <div className="categories-select">
            {(Array.isArray(allCategories) ? allCategories : []).map(cat => (
              <label key={cat.id} className="category-option">
                <input
                  type="checkbox"
                  checked={post.categories.map(Number).includes(Number(cat.id))}
                  onChange={() => handleCategoryToggle(cat.id)}
                />
                <span>{cat.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            <Save size={18} /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostEdit;